import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { formatMoney, formatRelative } from '../../utils/formatters';
import { useNavigate } from 'react-router-dom';
import { bucketLabel } from './mecLabels';

// Column labels lean on `bucketLabel()` where they map cleanly to a bucket,
// so the cross-shop table speaks the same plain-English language the rest of
// MEC just adopted in Phase 1.
const COLUMNS = [
  { key: 'shop',         label: 'Shop',                                                   numeric: false, align: 'left'  },
  { key: 'p',            label: 'Purchases (P)',                                          numeric: true,  align: 'right' },
  { key: 'a',            label: `Matched (A) — ${bucketLabel('matched_cogs')}`,           numeric: true,  align: 'right' },
  { key: 'delta_wip',    label: 'ΔWIP',                                                   numeric: true,  align: 'right' },
  { key: 'variance',     label: 'Variance',                                               numeric: true,  align: 'right' },
  { key: 'variance_pct', label: 'Variance %',                                             numeric: true,  align: 'right' },
  { key: 'tier_1_pct',   label: 'PO matched %',                                           numeric: true,  align: 'right' },
  { key: 'tier_2_pct',   label: 'Vendor-guess %',                                         numeric: true,  align: 'right' },
  { key: 'unmatched_pct',label: `${bucketLabel('leakage')} %`,                            numeric: true,  align: 'right' },
  { key: 'last_run',     label: 'Last Run',                                               numeric: false, align: 'right' },
];

function varianceTone(pct) {
  // Per spec: red threshold >10%, yellow >5%, neutral otherwise. Only
  // applied to ABS value — recovery (negative) is a good signal but
  // big-recovery is also worth surfacing.
  const a = Math.abs(pct || 0);
  if (a > 10) return 'danger';
  if (a > 5) return 'action';
  return 'neutral';
}

export default function PerShopTable({ rows, selectedShops, loading, runningShopIds = [] }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState({ key: 'variance', dir: 'desc' });

  const enriched = useMemo(() => {
    // Build a row for every selected shop, merging in computed numbers if
    // we have a close-run payload for it.
    const out = [];
    for (const s of selectedShops) {
      const r = rows[s.shop_id];
      const parts = r?.parts || null;
      const p = parts?.p_cents || 0;
      const a = parts?.a_total_cents != null
        ? parts.a_total_cents
        : (parts?.a_used_cents || 0) + (parts?.a_pending_return_cents || 0);
      const dWip = parts?.delta_wip_cents || 0;
      const variance = parts?.variance_cents != null
        ? parts.variance_cents
        : p - a - dWip;
      const tally = parts?.match_tally || {};
      const t1 = tally.total_matched_cogs_cents || 0; // not perfectly t1 — see below
      const t1Count = tally.tier_1 || 0;
      const t2Count = tally.tier_2 || 0;
      const t3 = (tally.tier_3_no_po || 0) + (tally.tier_3_po_unused || 0) + (tally.tier_3_po_not_in_tekmetric || 0);
      const denom = (t1Count + t2Count + t3) || 1;
      const tier1Pct = (t1Count / denom) * 100;
      const tier2Pct = (t2Count / denom) * 100;
      const unmatchedPct = (t3 / denom) * 100;
      const variancePct = p > 0 ? (variance / p) * 100 : 0;
      out.push({
        shop_id: s.shop_id,
        shop_name: s.shop_name || `Shop ${s.shop_id}`,
        has_data: !!parts,
        is_running: runningShopIds.includes(s.shop_id),
        error: r?.error || null,
        p,
        a,
        delta_wip: dWip,
        variance,
        variance_pct: variancePct,
        tier_1_pct: tier1Pct,
        tier_2_pct: tier2Pct,
        unmatched_pct: unmatchedPct,
        last_run: r?.created_at || r?.finalized_at || null,
      });
    }
    out.sort((x, y) => {
      const k = sort.key;
      let av = x[k];
      let bv = y[k];
      if (k === 'shop') { av = x.shop_name; bv = y.shop_name; }
      if (k === 'last_run') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return out;
  }, [rows, selectedShops, sort, runningShopIds]);

  const clickSort = (key) => {
    setSort((s) => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' }
    );
  };

  return (
    <Card padded={false}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-[var(--color-warm-100)] bg-[var(--color-cream-100)]">
              {COLUMNS.map((c) => {
                const active = sort.key === c.key;
                return (
                  <th
                    key={c.key}
                    className={`
                      px-4 py-2.5 text-[10px] font-bold uppercase tracking-[1.2px]
                      ${c.align === 'right' ? 'text-right' : 'text-left'}
                    `}
                  >
                    <button
                      type="button"
                      onClick={() => clickSort(c.key)}
                      className={`
                        inline-flex items-center gap-1
                        ${active ? 'text-[var(--color-warm-900)]' : 'text-[var(--color-warm-500)]'}
                        hover:text-[var(--color-warm-800)]
                      `}
                    >
                      {c.label}
                      {active && (sort.dir === 'desc' ? <ChevronDown size={11} /> : <ChevronUp size={11} />)}
                    </button>
                  </th>
                );
              })}
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {enriched.map((r) => {
              const vTone = varianceTone(r.variance_pct);
              return (
                <tr
                  key={r.shop_id}
                  onClick={() => navigate(`/mec/location/${r.shop_id}`)}
                  className="border-b border-[var(--color-warm-100)] last:border-b-0 hover:bg-[var(--color-cream-100)] transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3 align-middle">
                    <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
                      {r.shop_name}
                    </div>
                    <div className="text-[11px] text-[var(--color-warm-500)] mono-nums mt-0.5">
                      ID {r.shop_id}
                    </div>
                  </td>
                  {r.is_running ? (
                    <td colSpan={8} className="px-4 py-3 align-middle text-center text-[12px] text-[var(--color-warm-500)]">
                      <span className="inline-flex items-center gap-2">
                        <Loader2 size={13} className="animate-spin text-[var(--color-recovery-500)]" />
                        Running close…
                      </span>
                    </td>
                  ) : r.error ? (
                    <td colSpan={8} className="px-4 py-3 align-middle text-[12px] text-[var(--color-danger-700)]">
                      <span className="inline-flex items-center gap-2">
                        <AlertCircle size={13} />
                        {r.error}
                      </span>
                    </td>
                  ) : !r.has_data ? (
                    <td colSpan={8} className="px-4 py-3 align-middle text-[12px] text-[var(--color-warm-500)] italic">
                      No close run yet — click <strong className="not-italic font-semibold">Run Closes</strong> above.
                    </td>
                  ) : (
                    <>
                      <CellMoney value={r.p} />
                      <CellMoney value={r.a} />
                      <CellMoney value={r.delta_wip} signed />
                      <CellMoney value={r.variance} signed
                        tone={r.variance > 0 ? 'leakage' : (r.variance < 0 ? 'recovery' : 'neutral')} />
                      <td className="px-4 py-3 align-middle text-right">
                        <Badge tone={vTone} size="xs">
                          {r.variance_pct.toFixed(1)}%
                        </Badge>
                      </td>
                      <CellPct value={r.tier_1_pct} good />
                      <CellPct value={r.tier_2_pct} />
                      <CellPct value={r.unmatched_pct} flag />
                    </>
                  )}
                  <td className="px-4 py-3 align-middle text-right text-[11px] text-[var(--color-warm-500)]">
                    {r.last_run ? formatRelative(r.last_run) : '—'}
                  </td>
                  <td className="px-4 py-3 align-middle text-right">
                    <ArrowRight size={14} className="text-[var(--color-warm-400)] inline" />
                  </td>
                </tr>
              );
            })}
            {enriched.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-4 py-10 text-center text-[12px] text-[var(--color-warm-500)]">
                  {loading ? 'Loading shops…' : 'No shops selected. Pick at least one shop above.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CellMoney({ value, signed = false, tone = 'neutral' }) {
  const color = {
    neutral:  'text-[var(--color-warm-900)]',
    leakage:  'text-[var(--color-danger-700)]',
    recovery: 'text-[var(--color-recovery-700)]',
  }[tone];
  const display = value == null
    ? '—'
    : (signed && value > 0 ? '+' : '') + formatMoney(value / 100);
  return (
    <td className={`px-4 py-3 align-middle text-right tabular-nums font-semibold ${color}`}>
      {display}
    </td>
  );
}

function CellPct({ value, good = false, flag = false }) {
  const color = flag && value > 25
    ? 'text-[var(--color-danger-700)]'
    : good && value > 70
      ? 'text-[var(--color-recovery-700)]'
      : 'text-[var(--color-warm-700)]';
  return (
    <td className={`px-4 py-3 align-middle text-right tabular-nums ${color}`}>
      {(value || 0).toFixed(0)}%
    </td>
  );
}
